// src/auth/msalConfig.js
//
// Entra ID (Azure AD) application configuration.
//
// Everything tenant-specific lives in environment variables, NOT hardcoded.
// Vero is being built against the Embrace-IT tenant first and will move to
// Epoka's tenant later; when that happens only .env changes, no code.
//
// vue-cli only exposes variables prefixed with VUE_APP_ to the browser bundle.
// See .env.example for the values to set.

export const msalConfig = {
  auth: {
    clientId: process.env.VUE_APP_AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${process.env.VUE_APP_AZURE_TENANT_ID}`,

    // Must match a redirect URI registered under the "Single-page application"
    // platform in the app registration. Registered as Web instead, MSAL fails
    // on token exchange with a CORS error that looks unrelated to the cause.
    redirectUri: process.env.VUE_APP_AZURE_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: window.location.origin,

    // Return to the page the user started on rather than dumping them at the
    // redirect URI after login.
    navigateToLoginRequestUrl: true
  },

  cache: {
    // sessionStorage over localStorage: tokens die when the tab closes, which
    // is the safer default on shared or kiosk machines. Switch to
    // 'localStorage' only if single sign-on across tabs is actually wanted.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false
  },

  system: {
    loggerOptions: {
      loggerCallback(level, message, containsPii) {
        if (containsPii) return
        if (process.env.NODE_ENV !== 'development') return
        console.log(`[MSAL] ${message}`)
      },
      piiLoggingEnabled: false
    }
  }
}

// Scopes requested at sign-in. These match what was consented in the app
// registration. openid/profile/email are the standard OIDC scopes that produce
// the ID token; User.Read lets us call /me.
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read']
}

// Scopes for calling Microsoft Graph on behalf of the signed-in user.
// User.ReadBasic.All is needed to read OTHER users (the employee list and their
// photos), not just the signed-in one.
//
// NOTE: this whole delegated-Graph approach is being retired. Reading the
// directory from the browser means every user consents to User.Read.All, which
// is far broader than the four fields Vero uses. Once the backend reads Graph
// with application permissions, these scopes come out entirely.
export const graphRequest = {
  scopes: ['User.Read', 'User.ReadBasic.All', 'User.Read.All']
}

export const GRAPH_ENDPOINT = 'https://graph.microsoft.com/v1.0'

// ---------------------------------------------------------------------------
// Vero's own API
// ---------------------------------------------------------------------------

// The backend is exposed as an API in the same app registration, with a single
// delegated scope. Calls to Vero carry an access token whose audience is
// api://<client-id>, NOT the ID token.
//
// This matters: an ID token's audience is the SPA's client id, meaning "issued
// for the browser app". Sending it to an API is a known anti-pattern, because
// any app that can obtain an ID token for that client could then call the
// backend. An access token addressed to api://<client-id> can only be used
// against this API.
export const API_SCOPE = `api://${process.env.VUE_APP_AZURE_CLIENT_ID}/access_as_user`

export const apiRequest = {
  scopes: [API_SCOPE]
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

// Security group object ids. The backend derives the role from the same values
// and is the only thing that enforces it; these exist so the interface can hide
// what a person cannot reach, rather than showing them a page that 403s.
//
// Not secrets: a group id identifies a group, it does not grant membership.
export const ADMIN_GROUP_ID = process.env.VUE_APP_ADMIN_GROUP_ID
export const DISPLAY_GROUP_ID = process.env.VUE_APP_DISPLAY_GROUP_ID
